/**
 * A computer opponent.
 *
 * The bot reads a snapshot and answers with a BlobInput, nothing else: no
 * clock, no randomness beyond its own seeded generator, no access to the sim.
 * That keeps it a pure function of (state, seed), so a scripted rally against
 * it is reproducible and the tests below can pin its behaviour down.
 *
 * Three levels differ in how often the bot re-decides, how well it aims, and
 * whether it jumps and spends energy. Nothing here is clever: a volleyball bot
 * only has to be where the ball lands, and the levels mostly vary how late and
 * how wrong it is about that.
 */
import type { Constants } from './constants.js';
import { type BlobInput, type SimState, type Side, NO_INPUT } from './sim.js';

export type BotLevel = 'easy' | 'medium' | 'hard';

export const BOT_LEVELS: readonly BotLevel[] = ['easy', 'medium', 'hard'];

interface LevelProfile {
  /** Ticks between decisions. Reaction time, in effect. */
  interval: number;
  /** Aim error, metres, redrawn each decision. */
  error: number;
  /** Whether the bot targets the predicted landing spot or the ball's x. */
  predicts: boolean;
  /** Whether it jumps to meet the ball. */
  jumps: boolean;
  /** Energy it keeps in reserve before it will spend a charge; >1 never. */
  smashAbove: number;
  /** Horizontal offset it plays around, so the reflection points forward. */
  lean: number;
}

const PROFILES: Record<BotLevel, LevelProfile> = {
  easy: { interval: 20, error: 0.9, predicts: false, jumps: false, smashAbove: 2, lean: 0 },
  medium: { interval: 10, error: 0.4, predicts: true, jumps: true, smashAbove: 2, lean: 0.25 },
  hard: { interval: 4, error: 0.15, predicts: true, jumps: true, smashAbove: 0.3, lean: 0.35 },
};

/** xorshift32: tiny, deterministic, and plenty for aim jitter. */
function makeRandom(seed: number): () => number {
  let x = seed >>> 0 || 0x9e3779b9;
  return () => {
    x ^= x << 13;
    x >>>= 0;
    x ^= x >>> 17;
    x ^= x << 5;
    x >>>= 0;
    return x / 0x100000000;
  };
}

export class Bot {
  private readonly profile: LevelProfile;
  private readonly random: () => number;
  private targetX: number;
  private jitter = 0;
  private sinceDecision = Infinity;
  private input: BlobInput = NO_INPUT;

  constructor(
    readonly slot: number,
    readonly level: BotLevel,
    private readonly constants: Constants,
    seed = 1,
  ) {
    this.profile = PROFILES[level];
    this.random = makeRandom(seed);
    this.targetX = constants.courtWidth / 2;
  }

  /** Call once per sim tick with the current state. */
  think(state: SimState): BlobInput {
    const me = state.blobs[this.slot];
    if (!me) return NO_INPUT;
    const c = this.constants;
    const p = this.profile;

    this.sinceDecision++;
    if (this.sinceDecision >= p.interval) {
      this.sinceDecision = 0;
      this.jitter = (this.random() * 2 - 1) * p.error;
      this.targetX = this.chooseTarget(state, me.side) + this.jitter;
    }

    const dx = this.targetX - me.x;
    const deadband = 0.12;
    const left = dx < -deadband;
    const right = dx > deadband;

    const ball = state.ball;
    const dist = Math.hypot(ball.x - me.x, ball.y - me.y);
    const reach = c.blobRadius + c.ballRadius;
    const onMySide = (ball.x < c.courtWidth / 2 ? 0 : 1) === me.side;
    const closing = ball.vy < 0 && onMySide;

    // Jump when the ball is dropping into reach slightly above the dome. The
    // window is wide enough to be forgiving and narrow enough that the bot
    // does not bunny-hop.
    const jump =
      p.jumps &&
      me.grounded &&
      closing &&
      Math.abs(ball.x - me.x) < reach * 1.3 &&
      ball.y > reach + 0.6 &&
      ball.y < reach + 2.6;

    // Spend energy only on a spike that is about to happen, and only if the
    // bar is above the reserve. A low charged touch fires the ball almost
    // straight up, and holding smash otherwise just drains the bar.
    const attack =
      me.energy > p.smashAbove &&
      !me.grounded &&
      me.y >= c.smashMinHeight &&
      closing &&
      dist < reach + c.smashRange + 0.4;

    this.input = { left, right, jump, attack };
    return this.input;
  }

  /** Where to stand: under the landing spot, offset so the hit goes forward. */
  private chooseTarget(state: SimState, side: Side): number {
    const c = this.constants;
    const ball = state.ball;
    const half = c.courtWidth / 2;
    const lo = side === 0 ? c.blobRadius : half + c.netThickness / 2 + c.blobRadius;
    const hi = side === 0 ? half - c.netThickness / 2 - c.blobRadius : c.courtWidth - c.blobRadius;
    const clamp = (x: number): number => Math.min(hi, Math.max(lo, x));

    const onMySide = (ball.x < half ? 0 : 1) === side;
    const heading = side === 0 ? ball.vx < 0 : ball.vx > 0;
    if (!onMySide && !heading) {
      // Ball is theirs and going away: wait mid-court.
      return clamp(side === 0 ? half * 0.55 : c.courtWidth - half * 0.55);
    }

    const x = this.profile.predicts ? landingX(state, c) : ball.x;
    // Leaning to the outside of the ball puts the contact on the net-facing
    // flank of the dome, which sends the reflection over the net.
    const away = side === 0 ? -1 : 1;
    return clamp(x + away * this.profile.lean * c.blobRadius);
  }
}

/**
 * Where the ball will be at blob height if nothing touches it: ballistic
 * flight with side walls mirrored, Magnus and the net ignored. Good enough to
 * be under the ball, which is all a bot needs.
 */
export function landingX(state: SimState, c: Constants): number {
  const ball = state.ball;
  const g = c.gravity;
  const targetY = c.blobRadius + c.ballRadius;
  const dy = ball.y - targetY;
  if (dy <= 0) return ball.x;
  // Solve targetY = y + vy t + g t^2 / 2 for the later root.
  const disc = ball.vy * ball.vy - 2 * g * dy;
  if (disc < 0) return ball.x;
  const t = (-ball.vy - Math.sqrt(disc)) / g;
  if (!Number.isFinite(t) || t <= 0) return ball.x;

  let x = ball.x + ball.vx * t;
  const lo = c.ballRadius;
  const hi = c.courtWidth - c.ballRadius;
  const span = hi - lo;
  // Fold the line back into the court at each wall.
  x = ((x - lo) % (2 * span) + 2 * span) % (2 * span);
  if (x > span) x = 2 * span - x;
  return lo + x;
}

/** Ticks a bot at the given level takes to react; exported for the tests. */
export function reactionTicks(level: BotLevel): number {
  return PROFILES[level].interval;
}
